import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Switch,
  Alert 
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";

const PlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [dialogType, setDialogType] = useState(""); // "view" or "edit"
  const [editPlan, setEditPlan] = useState({});
  const [statusMessage, setStatusMessage] = useState(null);
  useEffect(() => {
    const fetchAllPlans = async () => {
      const twittertoken = localStorage.getItem("twitter_token");
      try {
        // Build the URL with a query parameter for the token
        const response = await fetch(
          `https://socialmedia.gtlit.com/api/get_all_plans?token=${twittertoken}`,
          {
            method: "POST",
          }
        );
        if (!response.ok) throw new Error(`Status: ${response.status}`);
        const data = await response.json();
        setPlans(data);
      } catch (err) {
        console.error("Failed to fetch plans:", err);
        setPlans([]);
      }
    };
    fetchAllPlans();
  }, []);


  // Actions for 3-dot menu
  const handleMenuOpen = (event, plan) => {
    setMenuAnchor(event.currentTarget);
    setSelectedPlan(plan);
  };
  const handleMenuClose = () => {
    setMenuAnchor(null);
    // setSelectedPlan(null);
  };

  // View, Edit, Delete Actions
  const handleView = (plan) => {
    setSelectedPlan(plan)
    setDialogType("view");
    handleMenuClose();
  };
  const handleEdit = () => {
    setEditPlan(selectedPlan);
    setDialogType("edit");
    handleMenuClose();
  };


  const handleDialogClose = () => {
    setDialogType("");
    setSelectedPlan(null);
    setEditPlan({});
  };

  const handleSavePlan = async () => {
    try {
      const action = dialogType === "create" ? "add" : "edit";
      const payload = {
        ...editPlan,
        action
      };
      const response = await fetch('/api/add_edit_plan', {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(`Status: ${response.status}`);
      // Optionally update local state after success
      if (dialogType === "create") {
        setPlans([...plans, { ...editPlan, id: plans.length ? Math.max(...plans.map(p => parseInt(p.id))) + 1 : 1 }]);
      } else {
        setPlans(plans.map((p) => (p.id === editPlan.id ? editPlan : p)));
      }
      handleDialogClose();
    } catch (err) {
      console.error("Failed to save plan:", err);
    }
  };

  const handleTogglePlanStatus = async (id) => {
    // Optimistically update local state
    setPlans((prevPlans) =>
      prevPlans.map((plan) =>
        plan.id === id
          ? { ...plan, status: Number(plan.status) === 1 ? 0 : 1 }
          : plan
      )
    );

    // Find the updated plan and determine new status value
    const updatedPlan = plans.find(p => p.id === id);
    const newStatus = Number(updatedPlan.status) === 1 ? 0 : 1;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/api/plan_status_update", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ id, status: newStatus }),
      });

      if (!response.ok) throw new Error();

      setStatusMessage({ text: "Plan status updated successfully!", type: "success" });

    } catch (error) {
      // Roll back local state on failure
      setPlans((prevPlans) =>
        prevPlans.map((plan) =>
          plan.id === id
            ? { ...plan, status: Number(plan.status) === 1 ? 0 : 1 }
            : plan
        )
      );
      setStatusMessage({ text: "Failed to update plan status. Please try again.", type: "error" });
    }
  };

  const userTypeLabels = {
    "2": "Agency",
    "3": "Agency User",
    "4": "Individual",
  };

  return (
    <Container maxWidth="100vw" sx={{ minHeight: "100vh", py: 5 }}>
      <Button
        variant="contained"
        color="primary"
        sx={{ float: "right", mb: 2 }}
        onClick={() => {
          setDialogType("create");
          setEditPlan({
            id: "",
            name: "",
            totalPosts: "",
            totalMessages: "",
            userType: "",
            price: "",
            totalAccountLimit: "",
            featureOne: "",
            featureTwo: "",
            icon: "",
            termsAndConditions: "",
            status: "",
            totalUsersLimit: ""
          }); // Empty initial state for new plan
        }}
      >
        Create Plan
      </Button>

      {statusMessage && (
        <Box sx={{ display: 'flex', mb: 2 }}>
          <Alert
            severity={statusMessage.type}
            onClose={() => setStatusMessage(null)}
            sx={{ width: '60%', justifyContent: 'center' }}
          >
            {statusMessage.text}
          </Alert>
        </Box>
      )}

      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        align="center"
        sx={{
          fontWeight: 700,
          color: "#5a189a",
          letterSpacing: 1.5
        }}
      >
        Plans
      </Typography>
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>ID</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>UserType</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Total Posts</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Total Messages</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Price</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.map((plan) => (
              <TableRow key={plan.id} hover>
                <TableCell>{plan.id}</TableCell>
                <TableCell>{userTypeLabels[plan.userType] || plan.userType}</TableCell>
                <TableCell>{plan.name}</TableCell>
                <TableCell>{plan.totalPosts}</TableCell>
                <TableCell>{plan.totalMessages}</TableCell>
                <TableCell>{plan.price}</TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Number(plan.status) === 1}
                        onChange={() => handleTogglePlanStatus(plan.id)}
                        color="success"
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#38b000",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#38b000",
                            opacity: 1,
                          },
                          "& .MuiSwitch-switchBase": {
                            transition: "all 0.3s cubic-bezier(.4,2,.5,.5)",
                          },
                        }}
                      />
                    }
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={(event) => handleMenuOpen(event, plan)}>
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {/* Action Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleView(selectedPlan)}>View</MenuItem>
          <MenuItem onClick={handleEdit}>Edit</MenuItem>
        </Menu>
      </TableContainer>

      {/* View Dialog */}
      <Dialog open={dialogType === "view"} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Plan Details</DialogTitle>
        <DialogContent dividers>
          {selectedPlan && Object.keys(selectedPlan).map((key) => (
            <Box key={key} mb={1}>
              <strong>{key}:</strong> {String(selectedPlan[key])}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={dialogType === "edit"} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Plan</DialogTitle>
        <DialogContent dividers>
          {editPlan && Object.keys(editPlan).map((key) => (
            <TextField
              key={key}
              margin="dense"
              label={key}
              type="text"
              fullWidth
              value={String(editPlan[key])}
              onChange={e => setEditPlan({ ...editPlan, [key]: e.target.value })}
              sx={{ mb: 2 }}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSavePlan} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/*Create Plan Dialog */}
      <Dialog open={dialogType === "create"} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Plan</DialogTitle>
        <DialogContent dividers>
          {Object.keys(editPlan).map((key) => (
            <TextField
              key={key}
              margin="dense"
              label={key}
              type="text"
              fullWidth
              value={String(editPlan[key])}
              onChange={e => setEditPlan({ ...editPlan, [key]: e.target.value })}
              sx={{ mb: 2 }}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            onClick={handleSavePlan}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>



    </Container>
  );
};

export default PlansPage;
